import { useMemo } from "react";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useFormParentContext } from "@/shared/contexts/FormParent";
import { Quote, Pet, CoverageSettings } from "@/shared/types/Quote.interface";
import { getAccidentCoverageItems, getIllnessSubSections } from "@/shared/components/FullCoverageDetailsModal/coverageData";
import { useBreedSpecificBadge } from "./useBreedSpecificBadge";
import { useHasBreedConditions } from "./useHasBreedConditions";
import { Species } from "@spotpetinsurance/spot-types/entities/Species";
import { useThemeContext } from "../contexts/ThemeProvider";

interface CoverageFeatureConfig {
    dental: boolean;
    showTelehealth: boolean;
    breedSpecific: boolean;
    compellingStats: boolean;
    microchipping: boolean;
}

interface UseCoverageDisplayItemsProps {
    coverageInfo?: CoverageSettings;
    species?: Pet["species"];
    samplePolicyUrl: string;
    isDentalCovered?: boolean;
    isMicrochippingCovered?: boolean;
    showBreedSpecificConditions?: boolean;
}

export function useCoverageDisplayItems(props: UseCoverageDisplayItemsProps) {
    const { coverageInfo, species, samplePolicyUrl, isDentalCovered, isMicrochippingCovered, showBreedSpecificConditions } = props;
    const { showRebrandTheme } = useThemeContext().theme;
    const { appState } = useAppLayerContext();
    const { watch } = useFormParentContext<Quote>();
    const policies = watch("policies");
    const { activePetIndex = 0, isMultiPetLinked } = appState;
    const activePet = policies?.[activePetIndex];

    const hasAccidentAndIllness = coverageInfo?.coverages?.some(c => c.type === "illness") ?? false;

    const featureConfig: CoverageFeatureConfig = {
        dental: isDentalCovered ?? false,
        microchipping: isMicrochippingCovered ?? false,
        breedSpecific: showBreedSpecificConditions ?? false,
        showTelehealth: true,
        compellingStats: true
    };

    const { getFilteredAccidentItems, getDisplayBreedName, getBreedSpecificBadge } = useBreedSpecificBadge({
        isMultiPetLinked,
        policies,
        activePetBreed: activePet?.breedName,
        enabled: featureConfig.breedSpecific
    });

    const hasAnyBreedConditions = useHasBreedConditions({
        isMultiPetLinked,
        policies,
        activePetBreed: activePet?.breedName,
        species: activePet?.species,
        hasAccidentAndIllness: hasAccidentAndIllness,
        isDentalCovered: featureConfig.dental,
        enabled: featureConfig.breedSpecific
    });

    const illnessSections = useMemo(() => {
        // If the plan does NOT have illness coverage, return an empty object immediately.
        if (!hasAccidentAndIllness) {
            return {};
        }

        const iconSize = "size-9 xs:size-12 sm:size-9 md:size-12 lg:size-9 xl:size-12";

        const allSections = getIllnessSubSections(iconSize, showRebrandTheme);
        if (!featureConfig.dental) {
            delete allSections.dentalIllnesses;
        }
        return allSections;
    }, [hasAccidentAndIllness, featureConfig.dental, showRebrandTheme]);

    const accidentItems = useMemo(() => {
        const currentSpecies = species ?? Species.Dog;
        const iconSize = "size-9 xs:size-12 sm:size-9 md:size-12 lg:size-9 xl:size-12";
        const allItems = getAccidentCoverageItems(currentSpecies, iconSize, hasAccidentAndIllness, showRebrandTheme);

        if (!featureConfig.microchipping || !hasAccidentAndIllness) {
            const treatmentsItem = allItems.find(item => item.id === "treatments");
            if (treatmentsItem) {
                treatmentsItem.description = treatmentsItem.description?.replace("microchipping, ", "");
            }
        }
        if (featureConfig.breedSpecific && hasAccidentAndIllness) {
            return getFilteredAccidentItems(allItems);
        }
        return allItems.filter(item => !item.replaces);
    }, [species, hasAccidentAndIllness, featureConfig.breedSpecific, featureConfig.microchipping, getFilteredAccidentItems, showRebrandTheme]);

    return {
        species,
        samplePolicyUrl,
        accidentItems,
        illnessSections,
        featureConfig,
        getDisplayBreedName,
        getBreedSpecificBadge,
        hasAnyBreedConditions
    };
}
